import React, { useEffect, useState } from "react";
import { Container, Table, Badge, Spinner, Card } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import { getTasksByUserAndOrg } from "../Api/TaskMaster";
import { getTicketAssignmentsByUserAndOrg } from "../Api/TicketApiOrg";
import { getTaskById } from "../Api/TaskMaster";
import { getTicketByTicketId } from "../Api/TicketApiOrg";

const MyAssignments = () => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;

  const [assignments, setAssignments] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [ticketAssignments, setTicketAssignments] = useState([]);

  const [noTaskData, setNoTaskData] = useState(false);
  const [noTicketData, setNoTicketData] = useState(false);

  const [loading, setLoading] = useState(false);

  const [noData, setNoData] = useState(false);

  // Load assignments
  const loadAssignments = async () => {
    if (!userId || !orgId) return;

    setLoading(true);
    setNoTaskData(false);
    setNoTicketData(false);

    try {
      const [taskData, ticketData] = await Promise.all([
        getTasksByUserAndOrg(userId, orgId),
        getTicketAssignmentsByUserAndOrg(userId, orgId),
      ]);

      /* ================= TASK ASSIGNMENTS ================= */

      const taskList = Array.isArray(taskData) ? taskData : [];

      // 🔹 Fetch task names
      const uniqueTaskIds = [
        ...new Set(taskList.map((t) => t.taskId).filter(Boolean)),
      ];

      const taskDetails = await Promise.all(
        uniqueTaskIds.map((id) => getTaskById(id).catch(() => null))
      );

      const taskNameMap = {};
      taskDetails.forEach((t) => {
        if (t?.taskId) taskNameMap[t.taskId] = t.taskName;
      });

      const enrichedTasks = taskList.map((t) => ({
        ...t,
        taskName: taskNameMap[t.taskId] || "-",
      }));

      setTaskAssignments(
        enrichedTasks.sort((a, b) => {
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return new Date(b.startTime || 0) - new Date(a.startTime || 0);
        })
      );

      setNoTaskData(enrichedTasks.length === 0);

      /* ================= TICKET ASSIGNMENTS ================= */

      const ticketList = Array.isArray(ticketData) ? ticketData : [];

      // 🔹 Fetch ticket names
      const uniqueTicketIds = [
        ...new Set(ticketList.map((t) => t.ticketId).filter(Boolean)),
      ];

      const ticketDetails = await Promise.all(
        uniqueTicketIds.map((id) => getTicketByTicketId(id).catch(() => null))
      );

      const ticketNameMap = {};
      ticketDetails.forEach((t) => {
        if (t?.ticketId) ticketNameMap[t.ticketId] = t.ticketName;
      });

      const enrichedTickets = ticketList.map((t) => ({
        ...t,
        ticketName: ticketNameMap[t.ticketId] || "-",
      }));

      setTicketAssignments(
        enrichedTickets.sort(
          (a, b) => new Date(b.startTime || 0) - new Date(a.startTime || 0)
        )
      );

      setNoTicketData(enrichedTickets.length === 0);
    } catch (err) {
      console.error("Assignment loading issue:", err);
      setNoTaskData(true);
      setNoTicketData(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments();
  }, [userId, orgId]);

  const getStatusBadge = (a) => {
    if (a.isPaused) return <Badge bg="warning">Paused</Badge>;
    if (a.isActive) return <Badge bg="success">Active</Badge>;
    return <Badge bg="secondary">Completed</Badge>;
  };

  const fmtDateTime = (d) => (d ? moment(d).format("MMM DD, YYYY HH:mm") : "-");
  const fmtHours = (h) => (h != null ? Number(h).toFixed(2) : "-");

  return (
    <Container fluid className="py-4">
      <Card className="shadow-sm">
        <Card.Body>
          <h4 className="mb-4">My Assignments</h4>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              {/* ================= TASK ASSIGNMENTS ================= */}
              <h5 className="mb-2">Task Assignments</h5>

              {noTaskData ? (
                <div className="text-center py-3">
                  <Badge bg="warning" className="p-2">
                    No task assignments to show
                  </Badge>
                </div>
              ) : (
                <div className="table-responsive mb-4">
                  <Table bordered hover size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Task ID</th>
                        <th>Task Name</th>
                        <th>Status</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Total Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskAssignments.map((a) => (
                        <tr key={a.assignmentId}>
                          <td>{a.taskId}</td>
                          <td>{a.taskName}</td>
                          <td>{getStatusBadge(a)}</td>
                          <td>{fmtDateTime(a.startTime)}</td>
                          <td>{fmtDateTime(a.endTime)}</td>
                          <td>{fmtHours(a.totalHours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* ================= TICKET ASSIGNMENTS ================= */}
              <h5 className="mb-2">Ticket Assignments</h5>

              {noTicketData ? (
                <div className="text-center py-3">
                  <Badge bg="warning" className="p-2">
                    No ticket assignments to show
                  </Badge>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table bordered hover size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Ticket ID</th>
                        <th>Ticket Name</th>
                        <th>Status</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Total Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticketAssignments.map((a) => (
                        <tr key={a.assignmentId}>
                          <td>{a.ticketId}</td>
                          <td>{a.ticketName}</td>
                          <td>{getStatusBadge(a)}</td>
                          <td>{fmtDateTime(a.startTime)}</td>
                          <td>{fmtDateTime(a.endTime)}</td>
                          <td>{fmtHours(a.totalHours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MyAssignments;
